import { useState } from "react";
import { useAppContext } from "../../../classes/contexts/AppContext";
import { useUser } from "../../../classes/contexts/UserContext";
import Button from "../../../components/button/Button";
import {
  openDocsResolvedPdfInNewTab,
  resolveEifu,
  resolveSla,
} from "../../../services/api/Docs";
import "./OnboardingPopup.css";

const OnboardingPopup = () => {
  const { hidePopup } = useAppContext();
  const { completeOnboarding } = useUser();
  const [checkboxes, setCheckboxes] = useState({
    sla: false,
    training: false,
    ifu: false,
    qualification: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckboxChange = key => {
    setCheckboxes(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const allChecked =
    checkboxes.sla &&
    checkboxes.training &&
    checkboxes.ifu &&
    checkboxes.qualification;

  const handleStart = async () => {
    if (allChecked && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await completeOnboarding();
        hidePopup();
      } catch (error) {
        console.error("Error completing onboarding:", error);
        // Still hide popup to not block the user
        hidePopup();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSlaClick = () => openDocsResolvedPdfInNewTab(resolveSla);

  const handleIfuClick = () => openDocsResolvedPdfInNewTab(resolveEifu);

  return (
    <div className="onboarding-popup">
      <div className="onboarding-popup-content">
        <div className="onboarding-popup-inner">
          <div className="onboarding-welcome-section">
            <h1 className="onboarding-title">Welcome to SPINEsim!</h1>
            <p className="onboarding-description">
              SPINEsim is your medical software platform for spine assessment
              and surgical planning.
            </p>
          </div>

          <div className="onboarding-acceptance-section">
            <p className="onboarding-instruction-text">
              Before using the software, please review the{" "}
              <button
                type="button"
                className="onboarding-link-button"
                onClick={handleIfuClick}
              >
                Instructions for Use (IFU)
              </button>{" "}
              and the{" "}
              <button
                type="button"
                className="onboarding-link-button"
                onClick={handleSlaClick}
              >
                Software License Agreement (SLA)
              </button>
              , then confirm the following:
            </p>

            <div className="onboarding-checkboxes">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={checkboxes.sla}
                  onChange={() => handleCheckboxChange("sla")}
                  disabled={isSubmitting}
                />
                <span>
                  I have read and agree to the{" "}
                  <span className="highlight-text">
                    Software License Agreement
                  </span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={checkboxes.training}
                  onChange={() => handleCheckboxChange("training")}
                  disabled={isSubmitting}
                />
                <span>
                  I have completed the required{" "}
                  <span className="highlight-text">training</span> to use
                  SPINEsim
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={checkboxes.ifu}
                  onChange={() => handleCheckboxChange("ifu")}
                  disabled={isSubmitting}
                />
                <span>
                  I agree to use the software according to the{" "}
                  <span className="highlight-text">IFU</span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={checkboxes.qualification}
                  onChange={() => handleCheckboxChange("qualification")}
                  disabled={isSubmitting}
                />
                <span>
                  I am a{" "}
                  <span className="highlight-text">
                    qualified healthcare professional
                  </span>{" "}
                  with the competencies required to use SPINEsim
                </span>
              </label>
            </div>
          </div>

          <div className="onboarding-action-section">
            <Button
              value={isSubmitting ? "Starting..." : "Let's start"}
              onClick={handleStart}
              disabled={!allChecked || isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPopup;
